import { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { defaultValues } from './useJobAdsFiltersStorage'
import type { JobAdsFormType } from '../../types/career.types'

type Props = {
  isMultiSelection: boolean
  isUnselectable?: boolean
  typeKey: keyof JobAdsFormType // 'contractType' | 'contractDuration' | etc...
}

export const useFiltersSelection = ({
  isMultiSelection,
  isUnselectable = false,
  typeKey,
}: Props) => {
  const { getValues, setValue } = useFormContext<JobAdsFormType>()
  const initialValue = getValues(typeKey) as string | string[]

  const [localValue, setLocalValue] = useState<string | string[] | undefined>(
    (initialValue ||
      defaultValues[typeKey] ||
      (isMultiSelection ? [] : undefined)) as string | string[] | undefined,
  )

  const updateLocalValue = useCallback(
    (newValue: string | string[]) => {
      if (isMultiSelection) {
        setLocalValue(newValue || [])
      } else {
        setLocalValue(newValue)
      }
    },
    [isMultiSelection],
  )

  useEffect(() => {
    updateLocalValue(initialValue)
  }, [initialValue, updateLocalValue])

  const handleItemClick = (itemValue: string) => {
    if (isMultiSelection) {
      const localList = localValue as string[]
      // unselect it
      if (localList.includes(itemValue)) {
        const newList = localList.filter((v) => v !== itemValue)
        setLocalValue(newList)
      } else {
        // or add it
        const newList = [...localList, itemValue]
        setLocalValue(newList)
      }
    } else {
      if (isUnselectable) {
        // pick it or unselect it
        setLocalValue(itemValue !== localValue ? itemValue : undefined)
      } else {
        setLocalValue(itemValue)
      }
    }
  }

  const resetLocalValue = () => {
    updateLocalValue(initialValue)
  }

  const saveValue = () => {
    setValue(typeKey, localValue)
  }

  return {
    handleItemClick,
    localValue,
    saveValue,
    resetLocalValue,
  }
}
